// §7 — Processor Sign-off. Ported from Workspace.jsx SignOffSection: the
// section-completeness checklist + the electronic-signature pad wired to the
// server `sign` action, plus the last-resort Defer-&-sign insurance flow
// (canDeferInsurance gate + required reason → `defer-insurance` then `sign`).
// The insurance defer intentionally lives here, not in the submit modal: the
// sign gate requires §4, so insurance must be resolved before signing.
import { useState } from 'react';
import { Icon } from '../atoms';
import { useDocOrderCtx } from '../context';
import { fmtDate } from '../format';

export interface SignOffCheck {
  ok: boolean;
  num: string;
  label: string;
  sub?: string | null;
  auto?: string | null;
}

interface SignOffProps {
  checks: SignOffCheck[];
  signed: boolean;
  signedByName: string | null;
  signedAt: string | null;
  canSign: boolean;
  signing: boolean;
  blockers: string[];
  stepNo: number;
  onSign: () => void;
  /** A1.4 — insurance is the sole remaining blocker and is deferrable. */
  canDeferInsurance?: boolean;
  /** The specific §4 fields + EOI doc still outstanding (for the prompt). */
  missingInsurance?: { fields: string[]; doc: boolean };
  /** Scroll the processor to the Insurance section to complete it instead. */
  onGoInsurance?: () => void;
  /** R4 #26 — per-fee reasons §5 blocks (feeDispositionBlocker), rendered
   *  under the locked pad so "Finish section 5" is never a mystery. */
  feeBlockerDetail?: string[];
}

export function SignOffSection({
  checks,
  signed,
  signedByName,
  signedAt,
  canSign,
  signing,
  blockers,
  stepNo,
  onSign,
  canDeferInsurance = false,
  missingInsurance = { fields: [], doc: false },
  onGoInsurance,
  feeBlockerDetail = []
}: SignOffProps) {
  const { editable, userName, deferInsurance, busy } = useDocOrderCtx();
  const remaining = checks.filter((c) => !c.ok).map((c) => c.num);
  // Everything but the (deferrable) Insurance task is done → offer the two-path
  // prompt (Complete it / Defer & sign) instead of the generic locked pad.
  const insuranceLast = editable && !canSign && canDeferInsurance;
  const lockedTip = editable && !canSign && !insuranceLast ? `Finish the remaining sections first: ${blockers.join(', ')}` : undefined;
  const [deferOpen, setDeferOpen] = useState(false);
  const [deferReason, setDeferReason] = useState('');
  const missParts: string[] = [];
  if (missingInsurance.fields.length) {
    missParts.push(`Section 4 — ${missingInsurance.fields.join(', ')}`);
  }
  if (missingInsurance.doc) {
    missParts.push('Evidence of Insurance document');
  }
  const confirmDeferSign = async () => {
    const reason = deferReason.trim();
    if (!reason) {
      return;
    }
    const res = await deferInsurance(reason);
    if (res.ok) {
      onSign();
      setDeferOpen(false);
      setDeferReason('');
    }
  };
  const padBtn: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 13px',
    borderRadius: 'var(--r-md)',
    fontSize: 12.5,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    border: '1px solid transparent',
    whiteSpace: 'nowrap'
  };

  return (
    <section className="ws-section" id="sec-signoff">
      <div className="card card-pad snapshot-compact">
        <div className="ws-section-head">
          <h3 className="ws-section-title">
            <span className="ws-step-no">{stepNo}</span>Processor Sign-off
          </h3>
        </div>
        <div className="banner info" style={{ marginBottom: 16 }}>
          <Icon name="alertCircle" size={16} className="banner-ico" />
          <span>
            The Processor submits this request. When the file is finalized, Closing confirms and sends the LO their copy to review final fees &amp;
            compensation.
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s-5)', alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {checks.map((c) => (
              <div
                key={c.label}
                style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 'var(--t-sm)', fontWeight: 600, color: c.ok ? 'var(--fg)' : 'var(--fg-muted)' }}
              >
                <span style={{ color: c.ok ? 'var(--ok)' : 'var(--fg-subtle)', display: 'inline-flex' }}>
                  <Icon name={c.ok ? 'checkCircle' : 'clock'} size={17} />
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, flex: 1, minWidth: 0 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--fg-subtle)', minWidth: 18 }}>{c.num}</span>
                  <span>{c.label}</span>
                  {c.sub && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--warn-fg)' }}>· {c.sub}</span>}
                  {c.auto && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--fg-subtle)' }}>· {c.auto}</span>}
                </span>
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: 'var(--t-sm)',
                fontWeight: 600,
                color: signed ? 'var(--fg)' : 'var(--fg-muted)',
                marginTop: 4,
                paddingTop: 10,
                borderTop: '1px dashed var(--border)'
              }}
            >
              <span style={{ color: signed ? 'var(--ok)' : 'var(--fg-subtle)', display: 'inline-flex' }}>
                <Icon name={signed ? 'checkCircle' : 'clock'} size={17} />
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--fg-subtle)', minWidth: 18 }}>{stepNo}</span>
                <span>Processor Sign-off</span>
              </span>
            </div>
          </div>
          <div>
            <span className="field-label" style={{ marginBottom: 8, display: 'block' }}>
              Electronic signature — {signedByName || userName}
            </span>
            <div
              className={`sign-pad ${signed ? 'signed' : ''} ${editable && !canSign ? 'sign-pad-locked' : ''}`}
              style={insuranceLast && !signed ? { height: 'auto', minHeight: 120, padding: 16 } : undefined}
              onClick={() => {
                if (canSign && !signing) {
onSign();
}
              }}
              title={lockedTip}
            >
              {signed ? (
                <div style={{ textAlign: 'center' }}>
                  <div className="sign-script">{signedByName || userName}</div>
                  <div className="rail-note" style={{ marginTop: 2 }}>
                    Signed {fmtDate(signedAt)}
                  </div>
                </div>
              ) : insuranceLast ? (
                <div style={{ width: '100%' }} onClick={(e) => e.stopPropagation()}>
                  {!deferOpen ? (
                    <div style={{ textAlign: 'center' }}>
                      <Icon name="lock" size={18} style={{ color: 'var(--fg-subtle)' }} />
                      <div style={{ marginTop: 6, fontSize: 'var(--t-sm)', fontWeight: 700 }}>One item left: Insurance</div>
                      <div style={{ marginTop: 5, fontSize: 12, fontWeight: 500, color: 'var(--warn-fg)', lineHeight: 1.5 }}>
                        Still needed: {missParts.join(' · ')}
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          style={{ ...padBtn, background: 'var(--bg-card)', borderColor: 'var(--border-strong)', color: 'var(--fg-muted)' }}
                          onClick={() => onGoInsurance?.()}
                        >
                          <Icon name="arrowUpRight" size={13} />Complete it
                        </button>
                        <button type="button" style={{ ...padBtn, background: 'var(--accent)', color: '#fff' }} onClick={() => setDeferOpen(true)}>
                          Defer &amp; sign<Icon name="arrowRight" size={13} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 'var(--t-sm)', fontWeight: 700, marginBottom: 4 }}>Defer Insurance — reason required</div>
                      <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginBottom: 8, lineHeight: 1.45 }}>
                        Closing cannot balance or release docs until the EOI is satisfied.
                      </div>
                      <textarea
                        className="recon-note"
                        rows={2}
                        value={deferReason}
                        placeholder="e.g. Binder ordered — awaiting EOI; borrower confirmed coverage is bound."
                        onChange={(e) => setDeferReason(e.target.value)}
                        style={{ width: '100%' }}
                      />
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                        <button
                          type="button"
                          style={{ ...padBtn, background: 'var(--bg-card)', borderColor: 'var(--border-strong)', color: 'var(--fg-muted)' }}
                          onClick={() => {
                            setDeferOpen(false);
                            setDeferReason('');
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          style={{ ...padBtn, background: 'var(--accent)', color: '#fff', opacity: deferReason.trim() && !busy ? 1 : 0.45, cursor: deferReason.trim() && !busy ? 'pointer' : 'not-allowed' }}
                          disabled={!deferReason.trim() || busy}
                          onClick={() => void confirmDeferSign()}
                        >
                          <Icon name="check" size={13} />Defer &amp; sign
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : editable && !canSign ? (
                <div style={{ textAlign: 'center', color: 'var(--fg-subtle)' }}>
                  <Icon name="lock" size={20} />
                  <div style={{ marginTop: 6, fontSize: 'var(--t-sm)', fontWeight: 600 }}>Finish section{remaining.length === 1 ? '' : 's'} {remaining.join(', ')} to sign</div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>
                  <Icon name="penTool" size={22} />
                  <div style={{ marginTop: 6, fontSize: 'var(--t-sm)', fontWeight: 600 }}>{editable ? (signing ? 'Signing…' : 'Click to sign') : 'Awaiting signature'}</div>
                </div>
              )}
            </div>
            {/* R4 #26 — name each fee still blocking §5 (the invisible
                Credit-Report refresh question mystified a real tester). */}
            {editable && !signed && !canSign && feeBlockerDetail.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--warn-fg)', lineHeight: 1.5 }}>
                {feeBlockerDetail.map((reason) => (
                  <div key={reason} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span aria-hidden style={{ fontSize: 10 }}>•</span>
                    <span>{reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
